import { useState, useRef, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { ChevronLeft, Send, QrCode, CheckCircle, XCircle, User, CalendarDays } from 'lucide-react';
import type { Rental, Message } from '@tools-beaver/shared';
import { RENTAL_STATUS_LABELS } from '@tools-beaver/shared';
import { apiClient } from '../api/client';
import { useAppStore } from '../store/useAppStore';
import toast from 'react-hot-toast';

const statusColors: Record<string, string> = {
  PENDING: 'bg-yellow-100 text-yellow-700',
  ACCEPTED: 'bg-blue-100 text-blue-700',
  ACTIVE: 'bg-green-100 text-green-700',
  RETURNED: 'bg-silver-100 text-silver-600',
  REJECTED: 'bg-red-100 text-red-700',
  CANCELLED: 'bg-silver-100 text-silver-500',
};

export function RentalDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAppStore();
  const queryClient = useQueryClient();
  const [msgText, setMsgText] = useState('');
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const { data: rental, isLoading } = useQuery<Rental>({
    queryKey: ['rental', id],
    queryFn: async () => {
      const { data } = await apiClient.get(`/rentals/${id}`);
      return data.data;
    },
    refetchInterval: 10_000,
  });

  const { data: qrCodes } = useQuery<{ pickupQrCode?: string; returnQrCode?: string }>({
    queryKey: ['rentalQR', id],
    queryFn: async () => {
      const { data } = await apiClient.get(`/rentals/${id}/qr-codes`);
      return data.data;
    },
    enabled:
      !!rental &&
      rental.borrowerId === user?.id &&
      ['ACCEPTED', 'ACTIVE'].includes(rental.status),
    refetchInterval: 50 * 60 * 1000,
  });

  const { data: messages } = useQuery<Message[]>({
    queryKey: ['rentalMessages', id],
    queryFn: async () => {
      const { data } = await apiClient.get(`/rentals/${id}/messages`);
      return data.data;
    },
    enabled:
      !!rental && ['ACCEPTED', 'ACTIVE', 'RETURNED'].includes(rental.status),
    refetchInterval: 5_000,
  });

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const acceptMutation = useMutation({
    mutationFn: async () => apiClient.post(`/rentals/${id}/accept`),
    onSuccess: () => {
      toast.success('Rental accepted! QR codes generated.');
      queryClient.invalidateQueries({ queryKey: ['rental', id] });
    },
    onError: (err: any) =>
      toast.error(err?.response?.data?.error?.message ?? 'Failed to accept'),
  });

  const rejectMutation = useMutation({
    mutationFn: async () => apiClient.post(`/rentals/${id}/reject`),
    onSuccess: () => {
      toast.success('Rental rejected.');
      queryClient.invalidateQueries({ queryKey: ['rental', id] });
    },
    onError: (err: any) =>
      toast.error(err?.response?.data?.error?.message ?? 'Failed to reject'),
  });

  const sendMutation = useMutation({
    mutationFn: async (content: string) =>
      apiClient.post(`/rentals/${id}/messages`, { content }),
    onSuccess: () => {
      setMsgText('');
      queryClient.invalidateQueries({ queryKey: ['rentalMessages', id] });
    },
    onError: (err: any) =>
      toast.error(err?.response?.data?.error?.message ?? 'Failed to send message'),
  });

  const handleSend = () => {
    const content = msgText.trim();
    if (!content) return;
    sendMutation.mutate(content);
  };

  if (isLoading) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-10 text-center text-silver-400">
        <div className="text-4xl animate-bounce-gentle mb-3">📅</div>
        <p>Loading rental details…</p>
      </div>
    );
  }

  if (!rental) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-10 text-center">
        <p className="text-silver-500 mb-4">Rental not found.</p>
        <Link to="/my-rentals" className="btn-primary">My Rentals</Link>
      </div>
    );
  }

  const isLender = rental.lenderId === user?.id;
  const isBorrower = rental.borrowerId === user?.id;
  const otherParty = isLender ? rental.borrower : rental.lender;
  const canMessage = ['ACCEPTED', 'ACTIVE', 'RETURNED'].includes(rental.status);

  return (
    <div className="max-w-2xl mx-auto px-4 py-6 space-y-4">
      <button
        onClick={() => navigate(-1)}
        className="flex items-center gap-1 text-sm text-silver-500 hover:text-silver-800 transition-colors"
      >
        <ChevronLeft size={16} /> Back
      </button>

      {/* Rental overview */}
      <div className="card p-5">
        <div className="flex items-start gap-4 mb-4">
          <div className="w-16 h-16 rounded-2xl bg-silver-100 overflow-hidden flex-shrink-0">
            {rental.tool?.images?.[0]?.url ? (
              <img
                src={rental.tool.images[0].url}
                alt={rental.tool.name}
                className="w-full h-full object-cover"
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center text-2xl">🔧</div>
            )}
          </div>
          <div className="flex-1 min-w-0">
            <p className="font-bold text-silver-800 truncate">
              {rental.tool?.name ?? 'Tool'}
            </p>
            <div className="flex items-center gap-2 mt-1 text-xs text-silver-400">
              <CalendarDays size={12} />
              <span>
                {new Date(rental.startDate).toLocaleDateString()} –{' '}
                {new Date(rental.endDate).toLocaleDateString()}
              </span>
            </div>
            <div className="flex items-center gap-2 mt-1.5">
              <span
                className={`badge text-xs ${
                  statusColors[rental.status] ?? 'bg-silver-100 text-silver-600'
                }`}
              >
                {RENTAL_STATUS_LABELS[rental.status] ?? rental.status}
              </span>
            </div>
          </div>
          <div className="text-right flex-shrink-0">
            <p className="font-bold text-silver-800">
              ${Number(rental.totalAmount).toFixed(2)}
            </p>
            <p className="text-xs text-silver-400">{rental.totalDays} days</p>
          </div>
        </div>

        {rental.borrowerNotes && (
          <div className="bg-silver-50 rounded-xl p-3 text-xs text-silver-600 mb-3">
            <span className="font-semibold">Borrower note: </span>
            {rental.borrowerNotes}
          </div>
        )}

        {/* Other party info */}
        {otherParty && (
          <div className="flex items-center gap-2 pt-3 border-t border-silver-100">
            <div className="w-7 h-7 rounded-full bg-silver-200 overflow-hidden flex-shrink-0">
              {otherParty.avatarUrl ? (
                <img src={otherParty.avatarUrl} alt="" className="w-full h-full object-cover" />
              ) : (
                <User size={14} className="text-silver-400 m-auto mt-1.5" />
              )}
            </div>
            <p className="text-xs text-silver-500">
              {isLender ? 'Borrower' : 'Lender'}:{' '}
              <span className="font-semibold text-silver-700">
                {otherParty.name ?? 'Anonymous'}
              </span>
            </p>
          </div>
        )}
      </div>

      {/* Lender actions for PENDING */}
      {isLender && rental.status === 'PENDING' && (
        <div className="card p-5">
          <h2 className="font-bold text-silver-800 mb-3">Review Request</h2>
          <p className="text-sm text-silver-500 mb-4">
            {rental.borrower?.name ?? 'Someone'} wants to rent your{' '}
            <strong>{rental.tool?.name}</strong>.
          </p>
          <div className="flex gap-3">
            <button
              onClick={() => rejectMutation.mutate()}
              disabled={rejectMutation.isPending || acceptMutation.isPending}
              className="btn-secondary flex-1 flex items-center justify-center gap-2"
            >
              <XCircle size={16} className="text-red-500" />
              Decline
            </button>
            <button
              onClick={() => acceptMutation.mutate()}
              disabled={acceptMutation.isPending || rejectMutation.isPending}
              className="btn-primary flex-1 flex items-center justify-center gap-2"
            >
              <CheckCircle size={16} />
              {acceptMutation.isPending ? 'Accepting…' : 'Accept'}
            </button>
          </div>
        </div>
      )}

      {/* QR codes for borrower */}
      {isBorrower && qrCodes && (
        <div className="card p-5">
          <h2 className="font-bold text-silver-800 mb-1 flex items-center gap-2">
            <QrCode size={18} className="text-garden-500" />
            {rental.status === 'ACCEPTED' ? 'Pickup QR Code' : 'Return QR Code'}
          </h2>
          <p className="text-xs text-silver-500 mb-4">
            {rental.status === 'ACCEPTED'
              ? 'Show this QR code to the lender when picking up the tool.'
              : 'Show this QR code to the lender when returning the tool.'}
          </p>

          {rental.status === 'ACCEPTED' && qrCodes.pickupQrCode && (
            <div className="flex flex-col items-center">
              <img
                src={qrCodes.pickupQrCode}
                alt="Pickup QR Code"
                className="w-52 h-52 rounded-2xl border-4 border-garden-200"
              />
              <p className="text-xs text-silver-400 mt-2">Expires in ~1 hour · Tap to refresh</p>
            </div>
          )}

          {rental.status === 'ACTIVE' && qrCodes.returnQrCode && (
            <div className="flex flex-col items-center">
              <img
                src={qrCodes.returnQrCode}
                alt="Return QR Code"
                className="w-52 h-52 rounded-2xl border-4 border-blue-200"
              />
              <p className="text-xs text-silver-400 mt-2">Expires in ~1 hour · Tap to refresh</p>
            </div>
          )}
        </div>
      )}

      {/* Messaging */}
      {canMessage && (
        <div className="card flex flex-col" style={{ minHeight: 300 }}>
          <div className="p-4 border-b border-silver-100">
            <h2 className="font-bold text-silver-800 text-sm">Messages</h2>
          </div>

          <div className="flex-1 overflow-y-auto p-4 space-y-3" style={{ maxHeight: 320 }}>
            {!messages?.length ? (
              <p className="text-center text-silver-400 text-xs py-8">
                No messages yet. Say hi to arrange pickup details!
              </p>
            ) : (
              messages.map((msg) => {
                const isMe = msg.senderId === user?.id;
                return (
                  <div
                    key={msg.id}
                    className={`flex gap-2 ${isMe ? 'flex-row-reverse' : 'flex-row'}`}
                  >
                    <div className="w-7 h-7 rounded-full bg-silver-200 overflow-hidden flex-shrink-0 mt-0.5">
                      {msg.sender?.avatarUrl ? (
                        <img src={msg.sender.avatarUrl} alt="" className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center text-xs">
                          {msg.sender?.name?.[0] ?? '?'}
                        </div>
                      )}
                    </div>
                    <div
                      className={`max-w-[75%] px-3 py-2 rounded-2xl text-sm ${
                        isMe
                          ? 'bg-garden-500 text-white rounded-tr-sm'
                          : 'bg-silver-100 text-silver-800 rounded-tl-sm'
                      }`}
                    >
                      {msg.content}
                    </div>
                  </div>
                );
              })
            )}
            <div ref={messagesEndRef} />
          </div>

          <div className="p-3 border-t border-silver-100 flex gap-2">
            <input
              type="text"
              value={msgText}
              onChange={(e) => setMsgText(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSend()}
              placeholder="Type a message…"
              className="input text-sm flex-1"
            />
            <button
              onClick={handleSend}
              disabled={!msgText.trim() || sendMutation.isPending}
              className="btn-primary px-4 py-2.5 disabled:opacity-40"
            >
              <Send size={16} />
            </button>
          </div>
        </div>
      )}

      {rental.status === 'RETURNED' && (
        <div className="card p-5 text-center">
          <div className="text-3xl mb-2">✅</div>
          <p className="font-semibold text-silver-800 mb-1">Rental Complete</p>
          <p className="text-sm text-silver-500">
            Returned on {rental.returnedAt ? new Date(rental.returnedAt).toLocaleDateString() : '—'}
          </p>
        </div>
      )}
    </div>
  );
}
